import {
  CCMC_Object,
  CCMC_Dependency,
  CCMC_Event,
  CCMC_Method,
  CCMC_FSM,
  CCMC_FSMCopy,
  CCMC_State,
  CCMC_Transition,
  CCMC_CalculateFSM,
  CCMC_Inheritance,
  CCMC_SpecialisedEvent,
} from '../metamodel';
import {
  createEDGNode,
  appendOETColumn,
  appendOETRow,
  createEDGLink,
  createFSMView,
  createFSMNode,
  createFSMLink,
  createEDGInheritanceLink,
} from '../uimodel';
import { ensureId } from '../metamodel/util';
import { getSnapshot } from 'mobx-state-tree';
import type { IModel, OrId, Position, Size } from '../types';
import type {
  AutoEventsAndMethods,
  DependencyType,
  MethodProperties,
  ObjectProperties,
  Specialisations,
  StateType,
} from '../types';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../stores/model/metamodel/method';
import type { IInheritance } from '../../../stores/model/metamodel/inheritance';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { IState } from '../../../stores/model/metamodel/state';
import type { ITransition } from '../../../stores/model/metamodel/transition';

// The subset of a CCMC_* result the FSM-view helpers consume: a freshly created
// FSM plus (for copies) the old-id -> new-element map.
interface FSMResult {
  fsm: IFSM;
}
interface FSMCopyResult extends FSMResult {
  idMapping: Record<string, IState | ITransition>;
}

// Positions and sizes for the default FSM
function positionAndSizeForState(state: IState): { position: Position; size: Size } {
  let position: Position;
  let size: Size;
  switch (state.type) {
    case 'INITIAL_STATE':
      position = { x: 30, y: 30 };
      size = { width: 15, height: 15 };
      break;
    case 'STATE':
      position = { x: 250, y: 200 };
      size = { width: 70, height: 35 };
      break;
    case 'FINAL_STATE':
    default:
      position = { x: 500, y: 30 };
      size = { width: 60, height: 35 };
      break;
  }
  return {
    position,
    size,
  };
}

// Create the FSM view and dependent UI model elements for a result
function createFSMViewForResult(model: IModel, result: FSMResult, distribute = false): void {
  const fsmview = createFSMView(model.uimodel, result.fsm);
  result.fsm.states.forEach((s, i) => {
    const { position: defaultPosition, size } = positionAndSizeForState(s);
    let position = defaultPosition;
    if (distribute) {
      position = {
        x: 30 + (i % 3) * 250,
        y: 30 + (Math.floor(i / 3) % 3) * 250,
      };
    }
    createFSMNode(fsmview, s, position, size);
  });
  result.fsm.transitions.forEach((t) => createFSMLink(fsmview, t));
}

function copyFSMViewForResult(
  model: IModel,
  result: FSMCopyResult,
  copyFromOrId: OrId<IFSM>
): void {
  const copyFromId = ensureId(copyFromOrId);
  const original = model.uimodel.findFSMById(copyFromId)!;
  const fsmview = createFSMView(model.uimodel, result.fsm);
  const idMapping = result.idMapping;
  original.nodes.forEach((n) => {
    const { state, position, size } = getSnapshot(n);
    // The reference id is a string uuid at runtime; idMapping maps the original
    // state id to the copied state.
    createFSMNode(fsmview, idMapping[state as string] as IState, position, size);
  });
  original.links.forEach((l) => {
    const { transition, vertices } = getSnapshot(l);
    // The reference id is a string uuid at runtime; idMapping maps the original
    // transition id to the copied transition.
    createFSMLink(fsmview, idMapping[transition as string] as ITransition, vertices);
  });
}

/**
 * Create a new object type and the EDG node and OET column for the new object
 * type.
 *
 * @param {Model} model The model to add to
 * @param {Object} properties The properties for the object. See CCMC_Object
 * documentation.
 * @param {Object} position The position. See createEDGNode documentation.
 * @param {*} methods_and_events The information about the methods and events to
 * be created. See CCMC_Object documentation.
 */
export function Combined_CreateObject(
  model: IModel,
  properties: ObjectProperties,
  position: Position | undefined,
  methods_and_events: AutoEventsAndMethods
) {
  // Execute the metacommand
  const result = CCMC_Object(model, properties, methods_and_events);
  // Execute the guicommands
  createEDGNode(model.uimodel, result.object, position);
  appendOETColumn(model.uimodel, result.object);
  result.events.forEach((e) => appendOETRow(model.uimodel, e));
  createFSMViewForResult(model, result);
  return result;
}

/**
 * Helper function to create an FSM link for the given transition.
 *
 * @param {Model} model The model do operation on.
 * @param {Transition} transition The transition to add an FSM link for.
 */
function executeCreateFSMLink(model: IModel, transition: ITransition): void {
  const fsm = transition.fsm;
  const fsmview = model.uimodel.findFSM(fsm)!;
  createFSMLink(fsmview, transition);
}

/**
 * Create a new dependency and the EDG link for it.
 *
 * @param {Model} model The model to operate on.
 * @param {String} masterId The id of the master object type.
 * @param {String} dependentId The id of the dependent object type.
 * @param {DEPENDENCY_TYPE} type The type of dependency
 * @param {Boolean} propagate Should methods be automatically propagated along
 * the new dependency?
 */
export function Combined_CreateDependency(
  model: IModel,
  masterId: string,
  dependentId: string,
  type: DependencyType,
  propagate: boolean
) {
  // Execute the metacommand
  const result = CCMC_Dependency(model, masterId, dependentId, type, propagate);
  // Execute the GUI commands
  createEDGLink(model.uimodel, result.dependency, []);
  for (const transition of result.transitions) {
    executeCreateFSMLink(model, transition);
  }
  return result;
}

/**
 * Create a new event type and the OET row for it.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the event.
 */
export function Combined_CreateEvent(model: IModel, name: string) {
  // Execute the metacommand
  const result = CCMC_Event(model, name);
  // Execute the guicommand
  appendOETRow(model.uimodel, result.event);
  return result;
}

/**
 * Create a new specialised event type and the OET row for it.
 *
 * @param {Model} model The model to operate on.
 * @param {String} name The name for the event.
 * @param {EventType or String} ownerEventTypeOrId The owner event or its id.
 * @param {Inheritance or String} inheritanceOrId The inheritance or its id.
 */
export function Combined_CreateSpecialisedEvent(
  model: IModel,
  name: string,
  ownerEventTypeOrId: OrId<IEventType>,
  inheritanceOrId: OrId<IInheritance>
) {
  // Execute the metacommand
  const result = CCMC_SpecialisedEvent(model, name, ownerEventTypeOrId, inheritanceOrId);
  // Execute the guicommand
  appendOETRow(model.uimodel, result.event);
  return result;
}

/**
 * Create a new method and the FSM links for the transitions
 * created as a consequence.
 *
 * @param {Model} model The model to operate on.
 * @param {Object} properties method properties. See CCMC_Method.
 * @param {Boolean} propagate should the method be propagated automatically?
 */
export function Combined_CreateMethod(
  model: IModel,
  properties: MethodProperties,
  propagate: boolean
) {
  // Execute the metacommand
  const result = CCMC_Method(model, properties, propagate);
  // Execute the GUI commands
  for (const transition of result.transitions) {
    executeCreateFSMLink(model, transition);
  }
  return result;
}

/**
 * Add the default FSM to a given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} objectOrId The object (or its id) for which to add the default FSM.
 */
export function Combined_CreateDefaultFSM(model: IModel, objectOrId: OrId<IObjectType>) {
  const result = CCMC_FSM(model, objectOrId, 'DEFAULT', 'DEFAULT', true);
  createFSMViewForResult(model, result);
  return result;
}

/**
 * Add an FSM to a given object type. Optionally copying an existing FSM.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} objectOrId The object (or its id) to which to add the FSM.
 * @param {String} name The name for the new FSM.
 * @param {String or FSM} copyFromOrdId The FSM (or its id) to copy (optional).
 */
export function Combined_CreateOrCopyFSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  name: string,
  copyFromOrId?: OrId<IFSM> | null
) {
  let result;
  if (copyFromOrId == null) {
    result = CCMC_FSM(model, objectOrId, name, 'USER_DEFINED', false);
    createFSMViewForResult(model, result);
  } else {
    result = CCMC_FSMCopy(model, objectOrId, name, 'USER_DEFINED', false, copyFromOrId);
    copyFSMViewForResult(model, result, copyFromOrId);
  }
  return result;
}

/**
 * Calculate an FSM from two given FSMs and add it to a given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} objectOrId The object (or its id) to which to add the FSM.
 * @param {String or FSM} fsm1OrId The first FSM (or its id).
 * @param {String or FSM} fsm2OrId The second FSM (or its id).
 * @param {String} name The name for the new FSM.
 */
export function Combined_CalculateFSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsm1OrId: OrId<IFSM>,
  fsm2OrId: OrId<IFSM>,
  name: string
) {
  // Execute the metacommand
  const result = CCMC_CalculateFSM(model, objectOrId, fsm1OrId, fsm2OrId, name);
  // Execute the GUI commands
  createFSMViewForResult(model, result, true);
  return result;
}

/**
 * Create a new state in the given fsm of the given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} objectOrId The object (or its id).
 * @param {String or FSM} fsmOrId The fsm (or its id).
 * @param {String} name The name for the new state.
 * @param {String} type The type for the new state.
 * @param {Object} position The position. See createFSMNode documentation.
 */
export function Combined_CreateState(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  name: string,
  type: StateType,
  position?: Position
) {
  // Execute the metacommand
  const result = CCMC_State(model, objectOrId, fsmOrId, name, type);
  // Execute the GUI command
  const state = result.state;
  const fsm = state.fsm;
  const fsmview = model.uimodel.findFSM(fsm)!;
  const { size } = positionAndSizeForState(state);
  createFSMNode(fsmview, state, position, size);
  return result;
}

/**
 * Create a new transition in the given fsm of the given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} objectOrId The object (or its id).
 * @param {String or FSM} fsmOrId The fsm (or its id).
 * @param {String or State} sourceOrId The source state (or its id).
 * @param {String or State} targetOrId The target state (or its id).
 * @param {String[] or Method[]} methodsOrIds the methods for the transition (or the ids).
 */
export function Combined_CreateTransition(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  sourceOrId: OrId<IState>,
  targetOrId: OrId<IState>,
  methodsOrIds: OrId<IMethod>[]
) {
  // Execute the metacommand
  const result = CCMC_Transition(model, objectOrId, fsmOrId, sourceOrId, targetOrId, methodsOrIds);
  // Execute the GUI command
  const transition = result.transition;
  const fsm = transition.fsm;
  const fsmview = model.uimodel.findFSM(fsm)!;
  createFSMLink(fsmview, transition, []);
  return result;
}

/**
 * Create a new inheritance relation.
 *
 * @param {Model} model The model to operate on.
 * @param {String or ObjectType} superTypeOrId The supertype (or its id).
 * @param {String or ObjectType} subTypeOrId The subtype (or its id).
 * @param {boolean} abstract Is the supertype abstract?
 * @param {Object} specialisations Information about how to specialise the event types/methods of the
 * supertype (see CCMC_Inheritance).
 */
export function Combined_CreateInheritance(
  model: IModel,
  superTypeOrId: OrId<IObjectType>,
  subTypeOrId: OrId<IObjectType>,
  abstract: boolean,
  specialisations: Specialisations
) {
  // Execute the metacommand
  const result = CCMC_Inheritance(model, superTypeOrId, subTypeOrId, abstract, specialisations);
  // Create an EDG link for the inheritance
  createEDGInheritanceLink(model.uimodel, result.inheritance, []);
  // Add oet (sub)rows for the created (specialised) events
  for (const event of result.events) {
    appendOETRow(model.uimodel, event);
  }
  // Add an FSM link for the created transitions
  for (const transition of result.transitions) {
    executeCreateFSMLink(model, transition);
  }
  return result;
}
